import { Repeater, types } from "react-bricks/frontend";
import styled from "styled-components";
import { color } from "@styles";
import { EventsBarItemProps } from "./EventsBarItem";
import { backgroundColorProp } from "../utils/backgroundColor";
import { verticalPadSideProp } from "../utils/verticalPadding";
import { mobile, tablet } from "components/templates/Breakpoints";

export interface BackgroundSectionProps {
  backgroundColor?: types.IColor;
  useBackgroundImage?: boolean;
  backgroundImageUrl?: types.IImageSource;
  showTopImage?: boolean;
  showHeaderSection?: boolean;
  showMapSection?: boolean;
}

export const BackgroundSection: types.Brick<BackgroundSectionProps> = ({
  backgroundColor,
  useBackgroundImage,
  backgroundImageUrl,
  showTopImage,
  showHeaderSection,
  showMapSection,
}) => {
  return (
    <Wrapper
      backgroundColor={backgroundColor?.color}
      backgroundImage={
        useBackgroundImage && backgroundImageUrl?.src ? backgroundImageUrl.src : undefined
      }
    >
      {showTopImage && (
        <TopImageSection>
          <Repeater propName="topImage" />
        </TopImageSection>
      )}
      {showHeaderSection && (
        <HeaderSection>
          <Repeater propName="header" />
        </HeaderSection>
      )}
      {showMapSection && (
        <MapSection>
          <Repeater propName="map" />
        </MapSection>
      )}
    </Wrapper>
  );
};

BackgroundSection.schema = {
  name: "background-section",
  label: "Background Section",
  category: "Background Components",
  previewImageUrl: "/static/bricks/background-section.png",
  repeaterItems: [
    {
      name: "topImage",
      itemType: "image-section",
      itemLabel: "Top Image",
      min: 1,
      max: 1,
    },
    {
      name: "header",
      itemType: "hero-text",
      itemLabel: "Header Section",
      min: 1,
      max: 1,
    },
    {
      name: "map",
      itemType: "map-section",
      itemLabel: "Map Section",
      min: 1,
      max: 1,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    backgroundColor: { color: color.white },
    showTopImage: true,
    showHeaderSection: true,
    showMapSection: true,
  }),
  // Define side edit props
  sideEditProps: [
    backgroundColorProp,
    {
      name: "useBackgroundImage",
      label: "Use Background Image?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "backgroundImageUrl",
      label: "Background Image",
      type: types.SideEditPropType.Image,
      show: (props) => props.useBackgroundImage,
    },
    {
      name: "showTopImage",
      label: "Show top image?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "showHeaderSection",
      label: "Show header section?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "showMapSection",
      label: "Show map section?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
  ],
};

const Wrapper = styled.div<{ backgroundColor: string; backgroundImage?: string }>`
  background: ${(p) =>
    p.backgroundImage ? `url(${p.backgroundImage}) center/cover no-repeat` : p.backgroundColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 3rem;
  margin: 7.5rem 10rem;

  ${tablet} {
    padding: 1rem;
    margin: 3rem 1rem;
  }
`;

const TopImageSection = styled.div`
  width: 100%;
  margin-bottom: 5rem;

  ${tablet} {
    margin-bottom: 3.5rem;
  }
`;

const HeaderSection = styled.div`
  width: 100%;
  margin-bottom: 5rem;

  ${tablet} {
    margin-bottom: 1.5rem;
  }

  div {
    ${mobile} {
      padding: 0;
    }
  }
`;

const MapSection = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
`;

export default BackgroundSection;
